import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Move, RotateCcw, Monitor, Smartphone } from 'lucide-react';

/**
 * Componente para seleccionar el punto focal de una imagen
 * El punto focal determina qué parte de la imagen será el centro visible
 * cuando la imagen se recorta con object-cover
 * 
 * @param {string} imageUrl - URL de la imagen
 * @param {object} focalPoint - Punto focal actual {x: 50, y: 50} (porcentajes)
 * @param {function} onChange - Callback cuando cambia el punto focal
 * @param {boolean} showMultiplePreviews - Mostrar previews para diferentes aspect ratios
 */
export default function FocalPointSelector({ 
  imageUrl, 
  focalPoint = { x: 50, y: 50 }, 
  onChange,
  showMultiplePreviews = true
}) {
  const imageRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleClick = (e) => {
    if (!imageRef.current) return;
    
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    onChange({
      x: Math.round(Math.max(0, Math.min(100, x))),
      y: Math.round(Math.max(0, Math.min(100, y)))
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    handleClick(e);
  };

  const resetFocalPoint = () => {
    onChange({ x: 50, y: 50 });
  };

  // Diferentes aspect ratios para preview
  const previewFormats = [
    { name: 'Desktop', ratio: '16/9', icon: Monitor, width: '160px' },
    { name: 'Móvil', ratio: '3/1', icon: Smartphone, width: '150px' },
    { name: 'Cuadrado', ratio: '1/1', icon: null, width: '80px' },
  ];

  return (
    <div className="space-y-3">
      {/* Selector de punto focal */}
      <div className="relative">
        <p className="text-sm text-gray-600 mb-2 flex items-center gap-2">
          <Move className="w-4 h-4" />
          Hacé click en la imagen para elegir el punto focal
        </p>
        <div 
          className="relative border rounded-lg overflow-hidden cursor-crosshair bg-gray-100"
          style={{ maxWidth: '350px' }}
          onMouseDown={(e) => {
            setIsDragging(true);
            handleClick(e);
          }}
          onMouseMove={handleMouseMove}
          onMouseUp={() => setIsDragging(false)}
          onMouseLeave={() => setIsDragging(false)}
        >
          <img 
            ref={imageRef}
            src={imageUrl} 
            alt="Seleccionar punto focal"
            className="w-full h-auto"
            draggable={false}
          />
          {/* Punto focal */}
          <div 
            className="absolute w-6 h-6 -ml-3 -mt-3 pointer-events-none"
            style={{ 
              left: `${focalPoint.x}%`, 
              top: `${focalPoint.y}%` 
            }}
          >
            <div className="w-full h-full rounded-full border-2 border-white shadow-lg bg-blue-500/50 animate-pulse" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-white shadow" />
            </div>
          </div>
          {/* Guías */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `
                linear-gradient(to right, transparent calc(${focalPoint.x}% - 1px), rgba(255,255,255,0.5) calc(${focalPoint.x}% - 1px), rgba(255,255,255,0.5) calc(${focalPoint.x}% + 1px), transparent calc(${focalPoint.x}% + 1px)),
                linear-gradient(to bottom, transparent calc(${focalPoint.y}% - 1px), rgba(255,255,255,0.5) calc(${focalPoint.y}% - 1px), rgba(255,255,255,0.5) calc(${focalPoint.y}% + 1px), transparent calc(${focalPoint.y}% + 1px))
              `
            }}
          />
        </div>
        
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-gray-500">
            Posición: {focalPoint.x}% x {focalPoint.y}%
          </span>
          <Button 
            type="button"
            variant="ghost" 
            size="sm" 
            onClick={resetFocalPoint}
            className="text-xs"
          >
            <RotateCcw className="w-3 h-3 mr-1" />
            Centrar
          </Button>
        </div>
      </div>

      {/* Previews múltiples */}
      {showMultiplePreviews && (
        <div>
          <p className="text-sm text-gray-600 mb-2">Vista previa del recorte:</p>
          <div className="flex flex-wrap gap-3">
            {previewFormats.map((format) => (
              <div key={format.name} className="text-center">
                <div 
                  className="border rounded-lg overflow-hidden bg-gray-100"
                  style={{ 
                    width: format.width, 
                    aspectRatio: format.ratio
                  }}
                >
                  <img 
                    src={imageUrl} 
                    alt={`Preview ${format.name}`}
                    className="w-full h-full object-cover"
                    style={{ 
                      objectPosition: `${focalPoint.x}% ${focalPoint.y}%` 
                    }}
                  />
                </div>
                <div className="flex items-center justify-center gap-1 mt-1">
                  {format.icon && <format.icon className="w-3 h-3 text-gray-400" />}
                  <span className="text-xs text-gray-500">{format.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
